/**
 * @file lib/git/actor-repository-manager.ts
 * @description アクターリポジトリの作成・初期化マネージャー
 * @author GFTD Co., Ltd.
 */

import { GitFileSystem, GitConfig } from './git-fs';
import { ChatGitManager } from './chat-git-manager';
import { createDefaultChatGitConfig } from './chat-history-utils';
import { ChatGitConfig } from '../types/chat-git';
import { ActorSession, ActorMessage } from '../types/session';
import { generateRepositoryName } from '../utils/uuid';

export interface ActorRepositoryConfig {
  name: string;
  description?: string;
  type: string;
  repositoryType: 'local' | 'online';
  repositoryUrl?: string;
  gitConfig: ChatGitConfig;
  localPath?: string;
  repositoryName?: string;  // UUID付きのリポジトリ名
}

export interface RepositoryInitResult {
  success: boolean;
  repositoryPath: string;
  gitConfig: ChatGitConfig;
  initialCommitSha?: string;
  error?: string;
}

/**
 * アクターリポジトリ管理クラス
 * ローカル・オンラインリポジトリの作成と初期化を管理
 */
export class ActorRepositoryManager {
  
  /**
   * 新しいアクターリポジトリを作成・初期化
   */
  static async createActorRepository(config: ActorRepositoryConfig): Promise<RepositoryInitResult> {
    try {
      if (config.repositoryType === 'local') {
        return await this.createLocalRepository(config);
      } else {
        return await this.initializeOnlineRepository(config);
      }
    } catch (error) {
      console.error('Failed to create actor repository:', error);
      return {
        success: false,
        repositoryPath: '',
        gitConfig: config.gitConfig,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * ローカルリポジトリを作成
   */
  private static async createLocalRepository(config: ActorRepositoryConfig): Promise<RepositoryInitResult> {
    // UUID付きのリポジトリ名を使用（既に生成済みの場合はそれを使用）
    const repositoryName = config.repositoryName || generateRepositoryName(config.name);
    const repositoryPath = config.localPath || `/actors/${repositoryName}`;
    
    // ローカル用のGit設定を作成
    const localGitConfig: GitConfig = {
      url: `file://${repositoryPath}`,
      token: '', // ローカルの場合はトークン不要
      author: config.gitConfig.git.author,
      branch: config.gitConfig.git.branch || 'main',
      dir: repositoryPath
    };

    try {
      // GitFileSystemでローカルリポジトリを初期化
      const gitFs = new GitFileSystem(localGitConfig);
      await gitFs.initialize();

      // 初期ファイルを作成
      await this.createInitialFiles(gitFs, config);

      // 初期コミットを作成
      const initialCommitSha = await gitFs.commit('Initial commit: Actor repository created');

      return {
        success: true,
        repositoryPath,
        gitConfig: {
          ...config.gitConfig,
          git: localGitConfig
        },
        initialCommitSha
      };

    } catch (error) {
      console.error('Failed to create local repository:', error);
      return {
        success: false,
        repositoryPath,
        gitConfig: config.gitConfig,
        error: error instanceof Error ? error.message : 'Failed to create local repository'
      };
    }
  }

  /**
   * オンラインリポジトリを初期化
   */
  private static async initializeOnlineRepository(config: ActorRepositoryConfig): Promise<RepositoryInitResult> {
    if (!config.repositoryUrl) {
      return {
        success: false,
        repositoryPath: '',
        gitConfig: config.gitConfig,
        error: 'Repository URL is required for online repositories'
      };
    }

    // UUID付きのリポジトリ名を使用（ローカル作業ディレクトリ名として）
    const repositoryName = config.repositoryName || generateRepositoryName(config.name);
    const repositoryPath = `/tmp/actors/${repositoryName}`;

    try {
      // GitFileSystemでオンラインリポジトリをクローンまたは初期化
      const gitFs = new GitFileSystem(config.gitConfig.git);
      await gitFs.initialize();

      // 初期ファイルを作成（リポジトリが空の場合）
      const hasFiles = await this.checkRepositoryHasFiles(gitFs);
      let initialCommitSha: string | undefined;

      if (!hasFiles) {
        await this.createInitialFiles(gitFs, config);
        initialCommitSha = await gitFs.commit('Initial commit: Actor repository setup');
        
        // リモートにプッシュ
        await gitFs.push();
      }

      return {
        success: true,
        repositoryPath,
        gitConfig: config.gitConfig,
        initialCommitSha
      };

    } catch (error) {
      console.error('Failed to initialize online repository:', error);
      return {
        success: false,
        repositoryPath,
        gitConfig: config.gitConfig,
        error: error instanceof Error ? error.message : 'Failed to initialize online repository'
      };
    }
  }

  /**
   * 初期ファイルを作成
   */
  private static async createInitialFiles(gitFs: GitFileSystem, config: ActorRepositoryConfig): Promise<void> {
    // README.md を作成
    const readmeContent = this.generateReadmeContent(config);
    await gitFs.writeFile('README.md', readmeContent);

    // .gitignore を作成
    const gitignoreContent = this.generateGitignoreContent();
    await gitFs.writeFile('.gitignore', gitignoreContent);

    // actor.config.json を作成
    const actorConfigContent = this.generateActorConfig(config);
    await gitFs.writeFile('actor.config.json', JSON.stringify(actorConfigContent, null, 2));

    // package.json を作成（Node.js系のactorの場合）
    if (this.shouldCreatePackageJson(config.type)) {
      const packageJsonContent = this.generatePackageJson(config);
      await gitFs.writeFile('package.json', JSON.stringify(packageJsonContent, null, 2));
    }

    // 基本的なディレクトリ構造を作成
    await this.createDirectoryStructure(gitFs, config);
  }

  /**
   * README.mdの内容を生成
   */
  private static generateReadmeContent(config: ActorRepositoryConfig): string {
    return `# ${config.name}

${config.description || 'Actor for automation and intelligent task processing'}

## Overview

This is an Actor repository created with the Actor SDK. Actors are intelligent automation agents that can process data, interact with APIs, and perform complex tasks.

**Actor Type**: ${config.type}
**Repository Type**: ${config.repositoryType}

## Features

- 🤖 Intelligent automation capabilities
- 📝 Chat history management with Git integration
- 🔄 Version control and collaboration
- 📊 Session tracking and analytics

## Setup

1. Install dependencies:
   \`\`\`bash
   npm install
   \`\`\`

2. Configure your environment variables in \`.env\`

3. Start the actor:
   \`\`\`bash
   npm start
   \`\`\`

## Development

### Project Structure

- \`src/\` - Source code
- \`sessions/\` - Chat history and session data
- \`config/\` - Configuration files
- \`tests/\` - Test files

### Commands

- \`npm run dev\` - Start development mode
- \`npm run build\` - Build for production
- \`npm run test\` - Run tests
- \`npm run lint\` - Check code quality

## Documentation

See the [Actor SDK Documentation](https://docs.actor.gftd.ai) for more information.

## Contributing

1. Create a feature branch
2. Make your changes
3. Add tests
4. Submit a pull request

## License

MIT License - see LICENSE file for details.

---

Generated by Actor SDK v1.0.0
`;
  }

  /**
   * .gitignoreの内容を生成
   */
  private static generateGitignoreContent(): string {
    return `# Dependencies
node_modules/
npm-debug.log*
yarn-debug.log*
yarn-error.log*

# Environment variables
.env
.env.local
.env.development.local
.env.test.local
.env.production.local

# Build outputs
dist/
build/
.next/
out/

# Cache and temp files
.cache/
.tmp/
.temp/

# IDE files
.vscode/
.idea/
*.swp
*.swo

# OS files
.DS_Store
Thumbs.db

# Logs
logs/
*.log

# Actor specific
sessions/temp/
*.session.tmp

# Git LFS
*.lfs.*
`;
  }

  /**
   * Actor設定ファイルを生成
   */
  private static generateActorConfig(config: ActorRepositoryConfig): any {
    return {
      name: config.name,
      description: config.description,
      type: config.type,
      version: "1.0.0",
      repository: {
        type: config.repositoryType,
        url: config.repositoryUrl,
        path: config.localPath
      },
      sdk: {
        version: "1.0.0",
        features: [
          "chat-history",
          "git-integration",
          "session-management"
        ]
      },
      capabilities: {
        chatHistory: {
          enabled: true,
          format: "both",
          autoCommit: true
        },
        sessionManagement: {
          enabled: true,
          maxSessions: 100,
          retentionDays: 30
        }
      },
      created: new Date().toISOString(),
      metadata: {
        tags: [config.type, "actor-sdk"],
        category: config.type,
        language: "typescript"
      }
    };
  }

  /**
   * package.jsonを生成
   */
  private static generatePackageJson(config: ActorRepositoryConfig): any {
    return {
      name: config.name,
      version: "1.0.0",
      description: config.description || `Actor for ${config.type}`,
      main: "dist/index.js",
      scripts: {
        "dev": "tsx watch src/index.ts",
        "build": "tsup src/index.ts --format cjs,esm --dts",
        "start": "node dist/index.js",
        "test": "vitest",
        "lint": "eslint src/",
        "type-check": "tsc --noEmit"
      },
      dependencies: {
        "@gftdcojp/actor-sdk-ui": "^1.0.0"
      },
      devDependencies: {
        "@types/node": "^20.0.0",
        "tsx": "^4.0.0",
        "tsup": "^8.0.0",
        "typescript": "^5.0.0",
        "vitest": "^1.0.0",
        "eslint": "^8.0.0"
      },
      keywords: [
        "actor",
        "automation",
        config.type,
        "actor-sdk"
      ],
      author: config.gitConfig.git.author.name,
      license: "MIT"
    };
  }

  /**
   * package.jsonを作成すべきかチェック
   */
  private static shouldCreatePackageJson(type: string): boolean {
    const nodeBasedTypes = [
      'chat-assistant',
      'api-client',
      'automation',
      'data-processor',
      'web-scraper'
    ];
    return nodeBasedTypes.includes(type);
  }

  /**
   * ディレクトリ構造を作成
   */
  private static async createDirectoryStructure(gitFs: GitFileSystem, config: ActorRepositoryConfig): Promise<void> {
    // 基本ディレクトリ
    await gitFs.writeFile('src/.gitkeep', '');
    await gitFs.writeFile('config/.gitkeep', '');
    await gitFs.writeFile('sessions/.gitkeep', '');
    await gitFs.writeFile('tests/.gitkeep', '');

    // 簡単なindex.tsファイルを作成
    if (this.shouldCreatePackageJson(config.type)) {
      const indexContent = this.generateIndexFile(config);
      await gitFs.writeFile('src/index.ts', indexContent);
    }
  }

  /**
   * index.tsファイルを生成
   */
  private static generateIndexFile(config: ActorRepositoryConfig): string {
    return `/**
 * ${config.name} - Actor Implementation
 * Generated by Actor SDK
 */

import { ActorSDK } from '@gftdcojp/actor-sdk-ui';

export class ${this.toPascalCase(config.name)}Actor {
  private sdk: ActorSDK;

  constructor() {
    this.sdk = new ActorSDK();
  }

  /**
   * Initialize the actor
   */
  async initialize(): Promise<void> {
    console.log('Initializing ${config.name} actor...');
    // Add your initialization logic here
  }

  /**
   * Process a message or task
   */
  async process(input: any): Promise<any> {
    console.log('Processing input:', input);
    
    // Add your processing logic here
    return {
      message: 'Hello from ${config.name}!',
      timestamp: new Date().toISOString(),
      input
    };
  }

  /**
   * Clean up resources
   */
  async cleanup(): Promise<void> {
    console.log('Cleaning up ${config.name} actor...');
    // Add your cleanup logic here
  }
}

// Export default instance
export default new ${this.toPascalCase(config.name)}Actor();
`;
  }

  /**
   * PascalCaseに変換
   */
  private static toPascalCase(str: string): string {
    return str
      .split(/[-_\s]+/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join('');
  }

  /**
   * リポジトリにファイルが存在するかチェック
   */
  private static async checkRepositoryHasFiles(gitFs: GitFileSystem): Promise<boolean> {
    try {
      const files = await gitFs.readDir('.');
      return files.length > 0;
    } catch {
      return false;
    }
  }

  /**
   * アクターリポジトリの設定を検証
   */
  static validateActorConfig(config: ActorRepositoryConfig): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!config.name || config.name.trim().length === 0) {
      errors.push('Actor name is required');
    }

    // アクター名の検証は緩和（表示名なので）
    if (config.name && config.name.trim().length > 100) {
      errors.push('Actor name must be 100 characters or less');
    }

    if (!config.type || config.type.trim().length === 0) {
      errors.push('Actor type is required');
    }

    if (!config.repositoryType) {
      errors.push('Repository type is required');
    }

    if (config.repositoryType === 'online' && !config.repositoryUrl) {
      errors.push('Repository URL is required for online repositories');
    }

    if (config.repositoryType === 'online' && config.repositoryUrl) {
      try {
        new URL(config.repositoryUrl);
      } catch {
        errors.push('Invalid repository URL format');
      }
    }

    if (!config.gitConfig?.git?.author?.name) {
      errors.push('Git author name is required');
    }

    if (!config.gitConfig?.git?.author?.email) {
      errors.push('Git author email is required');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }
} 